import Papa from 'papaparse';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { DataTable, SortableHeader } from 'shared/components/ui/data-table';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from 'shared/components/ui/dialog';
import { ScrollArea, ScrollBar } from 'shared/components/ui/scroll-area';
import { DownloadAttachmentIcon } from 'shared/assets/svg/menu-icons/DownloadAttachmentIcon';
import { ExpandAttachmentIcon } from 'shared/assets/svg/menu-icons/ExpandAttachmentIcon';
import colorsStore from 'shared/store/colorsStore';
import { useFileOperations } from '@dev-streetbeat/chat';

import type { File } from '@dev-streetbeat/chat';

import styles from './CsvFileArtifact.module.scss';

type Props = {
  file: File;
};

export const CsvFileArtifact: React.FC<Props> = ({ file }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [tableData, setTableData] = useState<{
    columnDefs: ColumnDef<string[]>[];
    rows: string[][];
  }>({ columnDefs: [], rows: [] });
  const [loading, setLoading] = useState(false);

  const { downloadFile } = useFileOperations();

  useEffect(() => {
    setLoading(true);
    downloadFile(file.id).then(async (response) => {
      const blob = response;

      const csvText = await blob.text();
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results) => {
          const columnDefs: ColumnDef<string[]>[] = [];
          for (let columnIdx in results.meta.fields) {
            const columnName = results.meta.fields[columnIdx];
            columnDefs.push({
              id: columnIdx,
              accessorKey: columnName,
              accessorFn: (row) => row[columnIdx],
              header: ({ column }) => {
                return <SortableHeader column={column} text={columnName} />;
              },
            });
          }
          const rowsArray = results.data.map((row) => {
            return Object.values(row);
          });
          setTableData({
            columnDefs: columnDefs,
            rows: rowsArray,
          });
          setLoading(false);
        },
      });
    });
  }, []);

  const download = async () => {
    const outputType = 'xlsx';
    const fileName = file.name.replace(/\.[^/.]+$/, `.${outputType}`);
    const fileId = file.id;
    const response = await downloadFile(fileId);

    const link = document.createElement('a');
    link.href = URL.createObjectURL(response);
    link.download = fileName;
    link.click();
  };

  if (!tableData.columnDefs) {
    return null;
  }

  return (
    <>
      <div className={styles.csvContainer}>
        <div className={styles.downloadButton} onClick={download}>
          <DownloadAttachmentIcon />
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <ExpandAttachmentIcon className={styles.expandButton} />
          </DialogTrigger>
          <DialogContent
            className={styles.dialogContent}
            style={{
              backgroundColor: colorsConfig.tableBackground,
            }}
          >
            <DialogTitle className={styles.dialogTitle}>{file.name}</DialogTitle>
            <ScrollArea className="max-h-screen pb-12">
              <DataTable
                key={'table_' + file.id + '_dialog'}
                loading={loading}
                columns={tableData.columnDefs}
                data={tableData.rows}
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
      <div id={file.id.toString()} className="csv-attachment-scroll-margin" />
    </>
  );
};
